import React from "react";
import { Input } from "reactstrap";

// Change #100677 - Paul: "need ability from day to month to year etc".
// A repeat interval is stored as RepeatIntervalDays (its length in days, so
// "does this repeat?" stays a simple > 0 test) plus RepeatIntervalUnit
// (DAY / WEEK / MONTH / YEAR). The backend steps MONTH / YEAR on the calendar,
// so "every 1 month" from the 15th stays on the 15th. Mirrors
// CompanyVehicleCheckLibrary::UNIT_DAYS.
export const REPEAT_UNIT_DAYS = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 };

const UNIT_OPTIONS = [
  { value: "DAY", one: "day", many: "days" },
  { value: "WEEK", one: "week", many: "weeks" },
  { value: "MONTH", one: "month", many: "months" },
  { value: "YEAR", one: "year", many: "years" },
];

// The unit to show for a stored interval. Rows saved before the unit existed
// have none: show whole weeks as weeks, anything else as days.
export const repeatUnitOf = (days, unit) => {
  const u = String(unit || "").toUpperCase();
  if (REPEAT_UNIT_DAYS[u]) return u;
  const n = Number(days) || 0;
  return n > 0 && n % 7 === 0 ? "WEEK" : "DAY";
};

// How many units the stored days make, e.g. 90 days as MONTH -> 3.
export const repeatCountOf = (days, unit) => {
  const n = Number(days) || 0;
  if (n <= 0) return "";
  const per = REPEAT_UNIT_DAYS[repeatUnitOf(days, unit)];
  return Math.max(1, Math.round(n / per));
};

// "Daily", "Weekly", "Every 3 months", "Yearly"...
export const repeatLabel = (days, unit) => {
  const n = Number(days) || 0;
  if (n <= 0) return "";
  const u = repeatUnitOf(days, unit);
  const count = repeatCountOf(days, unit);
  const opt = UNIT_OPTIONS.find((o) => o.value === u);
  if (count === 1) {
    return { DAY: "Daily", WEEK: "Weekly", MONTH: "Monthly", YEAR: "Yearly" }[u];
  }
  return `Every ${count} ${opt.many}`;
};

/**
 * Number + unit picker. `days` / `unit` are the stored values; onChange gets
 * { RepeatIntervalDays, RepeatIntervalUnit } ready to merge into the row.
 * Blank number = one-off (days ""; the picked unit is kept for when a number
 * is typed - callers send unit null for a one-off).
 */
export const RepeatIntervalInput = ({ days, unit, onChange, size }) => {
  const currentUnit = repeatUnitOf(days, unit);
  const count = repeatCountOf(days, unit);
  const sm = size === "sm";

  const emit = (nextCount, nextUnit) => {
    const c = Number(nextCount);
    if (!(c > 0)) {
      onChange({ RepeatIntervalDays: "", RepeatIntervalUnit: nextUnit });
      return;
    }
    onChange({
      RepeatIntervalDays: Math.round(c) * REPEAT_UNIT_DAYS[nextUnit],
      RepeatIntervalUnit: nextUnit,
    });
  };

  return (
    <div className="d-flex gap-2 align-items-center">
      <Input
        type="number"
        min={0}
        step={1}
        className={`form-control${sm ? " form-control-sm" : ""}`}
        style={{ width: sm ? "70px" : "110px" }}
        placeholder={sm ? "" : "Blank = one-off"}
        value={count}
        onChange={(e) => emit(e.target.value, currentUnit)}
      />
      <Input
        type="select"
        className={`form-select${sm ? " form-select-sm" : ""}`}
        style={{ width: sm ? "100px" : "140px" }}
        value={currentUnit}
        onChange={(e) =>
          count
            ? emit(count, e.target.value)
            : // No number yet: remember the unit, stay one-off.
              onChange({
                RepeatIntervalDays: "",
                RepeatIntervalUnit: e.target.value,
              })
        }
      >
        {UNIT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {Number(count) === 1 ? o.one : o.many}
          </option>
        ))}
      </Input>
    </div>
  );
};

export default RepeatIntervalInput;
